import { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import {
  getCheckInWorkersRequest,
  getCheckOutWorkersRequest,
  getSiteWorkersRequest,
} from '@vyce/core/src/api/time';
import { CheckInWorkerDTO, CheckOutWorkerDTO } from '@vyce/core/src/api/types';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { FormParticipantInvite, TimeAndAttendanceLocation } from '@vyce/core/src/types';

import { LocationAudience } from '../components/FormRecipientsModeStep';

const PAGE_SIZE = 100;
// Stops a runaway loop if the backend ever reports a count it can't page through.
const MAX_PAGES = 50;

type ClockedWorker = CheckInWorkerDTO | CheckOutWorkerDTO;

export interface LocationAudienceCounts {
  everyone: number | null;
  clockedInNow: number | null;
  clockedInToday: number | null;
}

const EMPTY_COUNTS: LocationAudienceCounts = { everyone: null, clockedInNow: null, clockedInToday: null };

const fetchAllPages = async <T>(
  fetchPage: (offset: number) => Promise<{ count: number; items: T[] }>
): Promise<T[]> => {
  const items: T[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const data = await fetchPage(page * PAGE_SIZE);
    items.push(...data.items);
    if (!data.items.length || items.length >= data.count) {
      break;
    }
  }
  return items;
};

// Local calendar day, in the naive format the time endpoints take (see NotClockedOutWidget).
// An ISO string with a `Z` suffix is rejected, so don't use toISOString() here.
const TIME_PERIOD_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const getTodayPeriod = () => ({
  gte: dayjs().startOf('day').format(TIME_PERIOD_FORMAT),
  lte: dayjs().endOf('day').format(TIME_PERIOD_FORMAT),
});

const uniqueByUser = (workers: ClockedWorker[]) => {
  const byUser = new Map<string, ClockedWorker>();
  workers.forEach(worker => {
    if (!byUser.has(worker.user_id)) {
      byUser.set(worker.user_id, worker);
    }
  });
  return Array.from(byUser.values());
};

const toParticipant = (worker: ClockedWorker): FormParticipantInvite => ({
  email: worker.email ?? '',
  phone: worker.phone ?? '',
  name: `${worker.first_name ?? ''} ${worker.last_name ?? ''}`.trim(),
  uid: worker.user_id,
  ...(worker.photo_url ? { avatarUrl: worker.photo_url } : {}),
  ...(worker.ni_number ? { niNumber: worker.ni_number } : {}),
});

/**
 * Counts and resolves the people at the selected locations for each audience option.
 * The clocked-in options are resolved again at Send, so the list matches that moment
 * rather than when the dialog was opened.
 */
export const useLocationAudience = (
  companyId: string | undefined,
  locations: TimeAndAttendanceLocation[]
) => {
  const [counts, setCounts] = useState<LocationAudienceCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const siteIdsKey = useMemo(
    () =>
      locations
        .map(location => location.uuid)
        .filter(Boolean)
        .sort()
        .join(','),
    [locations]
  );
  const debouncedSiteIdsKey = useDebounceValue(siteIdsKey, 300);

  const fetchCheckedIn = useCallback(
    (siteIds: string[], signal?: AbortSignal) =>
      fetchAllPages<CheckInWorkerDTO>(async offset => {
        const res = await getCheckInWorkersRequest({
          companyId: companyId as string,
          payload: { site_ids: siteIds, offset, limit: PAGE_SIZE },
          signal,
        });
        return res.data;
      }),
    [companyId]
  );

  // Assumes `time_period` filters on the clock-out time — not verified against the gateway.
  const fetchCheckedOutToday = useCallback(
    (siteIds: string[], signal?: AbortSignal) =>
      fetchAllPages<CheckOutWorkerDTO>(async offset => {
        const res = await getCheckOutWorkersRequest({
          companyId: companyId as string,
          payload: { site_ids: siteIds, offset, limit: PAGE_SIZE, time_period: getTodayPeriod() },
          signal,
        });
        return res.data;
      }),
    [companyId]
  );

  const fetchClockedWorkers = useCallback(
    async (siteIds: string[], audience: Exclude<LocationAudience, 'everyone'>) => {
      if (!companyId || !siteIds.length) {
        return [];
      }
      if (audience === 'clocked_in_now') {
        return uniqueByUser(await fetchCheckedIn(siteIds));
      }
      const [checkedIn, checkedOut] = await Promise.all([
        fetchCheckedIn(siteIds),
        fetchCheckedOutToday(siteIds),
      ]);
      return uniqueByUser([...checkedIn, ...checkedOut]);
    },
    [companyId, fetchCheckedIn, fetchCheckedOutToday]
  );

  useEffect(() => {
    // null until the first debounce tick; '' once every location is deselected
    if (debouncedSiteIdsKey === null) {
      return;
    }
    const siteIds = debouncedSiteIdsKey ? debouncedSiteIdsKey.split(',') : [];
    if (!companyId || !siteIds.length) {
      setCounts(EMPTY_COUNTS);
      return;
    }

    const controller = new AbortController();
    const loadCounts = async () => {
      setLoading(true);
      setCounts(EMPTY_COUNTS);
      try {
        // Settled separately, so one failing endpoint doesn't blank the other counts.
        const [memberCounts, checkedIn, checkedOut] = await Promise.allSettled([
          // Sums per-site members, so someone linked to two selected sites counts twice.
          Promise.all(
            siteIds.map(siteId =>
              getSiteWorkersRequest({ companyId, payload: { site_id: siteId, offset: 0, limit: 1 } })
            )
          ),
          fetchCheckedIn(siteIds, controller.signal),
          fetchCheckedOutToday(siteIds, controller.signal),
        ]);
        if (controller.signal.aborted) {
          return;
        }
        [memberCounts, checkedIn, checkedOut].forEach(result => {
          if (result.status === 'rejected') {
            console.error(result.reason);
          }
        });
        const checkedInWorkers = checkedIn.status === 'fulfilled' ? checkedIn.value : null;
        const checkedOutWorkers = checkedOut.status === 'fulfilled' ? checkedOut.value : null;
        setCounts({
          everyone:
            memberCounts.status === 'fulfilled'
              ? memberCounts.value.reduce((total, res) => total + (res.data?.count ?? 0), 0)
              : null,
          clockedInNow: checkedInWorkers ? uniqueByUser(checkedInWorkers).length : null,
          clockedInToday:
            checkedInWorkers && checkedOutWorkers
              ? uniqueByUser([...checkedInWorkers, ...checkedOutWorkers]).length
              : null,
        });
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadCounts();
    return () => controller.abort();
  }, [companyId, debouncedSiteIdsKey, fetchCheckedIn, fetchCheckedOutToday]);

  /** Fresh list for a clocked-in option. People with neither email nor phone can't be sent to. */
  const resolveParticipants = useCallback(
    async (audience: Exclude<LocationAudience, 'everyone'>) => {
      const siteIds = siteIdsKey ? siteIdsKey.split(',') : [];
      const workers = await fetchClockedWorkers(siteIds, audience);
      const reachable = workers.filter(worker => worker.email || worker.phone);
      return {
        participants: reachable.map(toParticipant),
        skipped: workers.length - reachable.length,
      };
    },
    [siteIdsKey, fetchClockedWorkers]
  );

  return { counts, loading, resolveParticipants };
};
